import React, { useState } from 'react';
import { Image } from 'lucide-react';
import clsx from 'clsx';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import { getImageUrl } from '@utils/helpers';

interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  placeholder?: string;
  lazy?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  className,
  sizes = '100vw',
  placeholder,
  lazy = true,
  onLoad,
  onError,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  const shouldLoad = !lazy || isVisible;

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const imageSrc = getImageUrl(src);
  const placeholderSrc = placeholder || '/placeholder-image.jpg';

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className={clsx('relative overflow-hidden', className)}>
      {shouldLoad ? (
        <>
          {!isLoaded && !hasError && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
          )}

          <img
            src={hasError ? placeholderSrc : imageSrc}
            alt={alt}
            sizes={sizes}
            onLoad={handleLoad}
            onError={handleError}
            className={clsx(
              'w-full h-full object-cover transition-opacity duration-300',
              isLoaded || hasError ? 'opacity-100' : 'opacity-0'
            )}
            loading="lazy"
          />

          {hasError && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Image className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Image not available</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <Image className="w-8 h-8 text-gray-400" />
        </div>
      )}
    </div>
  );
};

export default ResponsiveImage;
